import { inject, injectable } from "inversify";
import { DependencyType } from "@di/DependencyType";
import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import {
  AdminSetUserPasswordDocument,
  AdminSetUserPasswordMutation,
  AdminSetUserPasswordMutationVariables,
  AssignableRolesDocument,
  AssignableRolesQuery,
  AssignableRolesQueryVariables,
  GetUsersDocument,
  GetUsersQuery,
  GetUsersQueryVariables,
  ImpersonateUserDocument,
  ImpersonateUserQuery,
  ImpersonateUserQueryVariables,
  SetUserRoleDocument,
  SetUserRoleMutation,
  SetUserRoleMutationVariables,
  SortEnumType,
  UserFilterInput,
  UserSortInput,
} from "generated/graphql";

type UserNode = NonNullable<
  NonNullable<GetUsersQuery["users"]>["nodes"]
>[number];

export type UserSortKey = "fullName" | "email" | "createdAt";

export interface PagedUsersResult {
  users: UserNode[];
  hasNextPage: boolean;
  endCursor?: string | null;
}

export interface GetUsersOptions {
  after?: string | null;
  searchTerm?: string;
  role?: string | null;
  organisationId?: string | null;
  sortKey: UserSortKey;
  sortDirection: "ASC" | "DESC";
}

@injectable()
export class UserService {
  constructor(
    @inject(DependencyType.ApolloClient)
    private readonly apolloClient: ApolloClient<NormalizedCacheObject>
  ) {}

  async getUsers(options: GetUsersOptions): Promise<PagedUsersResult> {
    const normalizedSearchTerm = options.searchTerm?.trim();
    const where: UserFilterInput | null = normalizedSearchTerm
      ? {
          or: [
            { fullName: { contains: normalizedSearchTerm } },
            { email: { contains: normalizedSearchTerm } },
          ],
        }
      : null;

    const sortValue =
      options.sortDirection === "ASC" ? SortEnumType.Asc : SortEnumType.Desc;
    const sortInput: UserSortInput =
      options.sortKey === "email"
        ? { email: sortValue }
        : options.sortKey === "createdAt"
          ? { createdAt: sortValue }
          : { fullName: sortValue };

    const queryTuple = await this.apolloClient.query<
      GetUsersQuery,
      GetUsersQueryVariables
    >({
      query: GetUsersDocument,
      variables: {
        after: options.after ?? null,
        where,
        order: [sortInput],
        role: options.role ?? null,
        organisationId: options.organisationId ?? null,
      },
      fetchPolicy: "network-only",
    });

    if (queryTuple.errors) {
      throw new Error("Failed to get users");
    }

    const connection = queryTuple.data.users;
    return {
      users: connection?.nodes?.filter(Boolean) ?? [],
      hasNextPage: Boolean(connection?.pageInfo.hasNextPage),
      endCursor: connection?.pageInfo.endCursor,
    };
  }

  async impersonateUser(userId: string): Promise<string> {
    const result = await this.apolloClient.query<
      ImpersonateUserQuery,
      ImpersonateUserQueryVariables
    >({
      query: ImpersonateUserDocument,
      variables: { userId },
      fetchPolicy: "network-only",
    });

    if (result.errors) {
      throw new Error(
        result.errors.map((e) => e.message).join(", ") ||
          "Failed to impersonate user"
      );
    }

    const token = result.data.impersonateUser;
    if (!token) {
      throw new Error("No token returned from impersonation query.");
    }

    return token;
  }

  async adminSetUserPassword(userId: string, password: string): Promise<void> {
    const result = await this.apolloClient.mutate<
      AdminSetUserPasswordMutation,
      AdminSetUserPasswordMutationVariables
    >({
      mutation: AdminSetUserPasswordDocument,
      variables: {
        input: { userId, password },
      },
    });

    if (result.errors || !result.data?.adminSetUserPassword) {
      throw new Error(
        result.errors?.map((error) => error.message).join(", ") ||
          "Failed to set password"
      );
    }
  }

  async getAssignableRoles(): Promise<string[]> {
    const result = await this.apolloClient.query<
      AssignableRolesQuery,
      AssignableRolesQueryVariables
    >({
      query: AssignableRolesDocument,
      fetchPolicy: "cache-first",
    });

    if (result.errors) {
      throw new Error(
        result.errors.map((error) => error.message).join(", ") ||
          "Failed to load roles"
      );
    }

    return result.data.assignableRoles;
  }

  async setUserRole(userId: string, role: string): Promise<string[]> {
    const result = await this.apolloClient.mutate<
      SetUserRoleMutation,
      SetUserRoleMutationVariables
    >({
      mutation: SetUserRoleDocument,
      variables: {
        input: { userId, role },
      },
    });

    if (result.errors || !result.data?.setUserRole) {
      throw new Error(
        result.errors?.map((error) => error.message).join(", ") ||
          "Failed to change role"
      );
    }

    return result.data.setUserRole.roles;
  }
}
